import { inspectionsService } from '@/services/inspections';
import type { DecideInspectionPayload } from '@/services/inspections/types';
import { s3Service } from '@/services/s3';
import type { UploadedFile } from '@/shared/components/form-fields/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface DecisionInspectionInput extends Omit<
  DecideInspectionPayload,
  'attachmentsFinalDecision' | 'attachmentsImprovementLetter'
> {
  finalDecisionAttachments?: UploadedFile[];
  improvementLetterAttachments?: UploadedFile[];
}

export function useDecisionInspection(policyId: string, inspectionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      finalDecisionAttachments,
      improvementLetterAttachments,
      ...rest
    }: DecisionInspectionInput) => {
      const prefix = `inspections/decision/${policyId}/${inspectionId}`;

      const [finalDecisionKeys, improvementLetterKeys] = await Promise.all([
        s3Service.uploadFiles(finalDecisionAttachments ?? [], prefix),
        s3Service.uploadFiles(improvementLetterAttachments ?? [], prefix),
      ]);

      const payload: DecideInspectionPayload = {
        ...rest,
        ...(finalDecisionKeys.length > 0 && {
          attachmentsFinalDecision: finalDecisionKeys,
        }),
        ...(improvementLetterKeys.length > 0 && {
          attachmentsImprovementLetter: improvementLetterKeys,
        }),
      };

      const response = await inspectionsService.decideInspection(
        policyId,
        inspectionId,
        payload
      );

      if (!response.success) {
        throw new Error(
          response.error ||
            'Erro ao registrar follow-up, tente novamente mais tarde'
        );
      }

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['inspections'] });
      await queryClient.invalidateQueries({
        queryKey: ['inspections-summary'],
      });
      await queryClient.invalidateQueries({ queryKey: ['inspection-records'] });
      await queryClient.invalidateQueries({
        queryKey: ['policy-inspections', policyId],
      });
    },
  });
}
